/**
 * ============================================================================
 * VINPOP — Wine Taste Map V2 (Interactive Sliders)
 * ============================================================================
 *
 * Interactive version of the taste map. Instead of quiz answers fixing the
 * user position, the user drags cursors on each of 3 axes. Products are
 * scored and filtered in real-time as the cursors move.
 *
 * Axes:
 *   X = Sweetness / Acidity   (Fresh/Acidic ↔ Sweet/Round)
 *   Y = Body / Structure       (Light ↔ Full/Powerful)
 *   Z = Intensity / Complexity (Subtle ↔ Intense/Complex)
 *
 * Data source: api.vinpop.fr /api/wines/getMapAll
 * CSS: wine-taste-map-v2.css
 * Filter system: see FILTER_SYSTEM.md for full architecture
 * Loaded from: main-collection-product-grid.liquid (conditional on quizz)
 * ============================================================================
 */
(function () {
  'use strict';

  // ── i18n ──────────────────────────────────────────────
  var TRANSLATIONS = {
    nl: {
      axXPos: 'Zoet / Rond', axXNeg: 'Fris / Zuur',
      axYPos: 'Vol / Krachtig', axYNeg: 'Licht',
      axZPos: 'Intens / Complex', axZNeg: 'Subtiel',
      barTitleX: 'ZOETHEID / ZUURGRAAD', barTitleY: 'BODY / STRUCTUUR', barTitleZ: 'INTENSITEIT / COMPLEXITEIT',
      barDescX: '🍋 Fris & zuur of 🥛 zacht & romig?', barDescY: '🍵 Licht als thee of ☕ sterk als espresso?', barDescZ: '🧀 Mild als brie of intens als oude kaas?',
      guideXLeft: '🍋 Citroen & azijn', guideXMid: '🫒 Olijfolie & zout', guideXRight: '🥛 Romige mayo',
      guideYLeft: '🍵 Thee / water', guideYMid: '☕ Cappuccino', guideYRight: '☕ Espresso',
      guideZLeft: '🧀 Brie / Mozzarella', guideZMid: '🧀 Jonge kaas', guideZRight: '🧀 Oude Parmezaan / Gouda',
      userLabel: 'JIJ',
      ariaLabel: 'Smaakprofiel Verkenner',
      legendUser: 'Sleep je positie', legendTop3: 'Top 3 matches', legendOtherRec: 'Andere matches', legendOther: 'Overige wijnen',
      explanation: 'Welke smaken spreken je aan?',
      loading: 'Wijndata laden...',
      dragHint: 'Sleep mij →',
      matchLabel: 'match',
      winesShown: '{count} wijnen gesorteerd op match'
    },
    en: {
      axXPos: 'Sweet / Round', axXNeg: 'Fresh / Acidic',
      axYPos: 'Full / Powerful', axYNeg: 'Light',
      axZPos: 'Intense / Complex', axZNeg: 'Subtle',
      barTitleX: 'SWEETNESS / ACIDITY', barTitleY: 'BODY / STRUCTURE', barTitleZ: 'INTENSITY / COMPLEXITY',
      barDescX: '🍋 Zesty & fresh or 🥛 smooth & creamy?', barDescY: '🍵 Light as tea or ☕ bold as espresso?', barDescZ: '🧀 Mild as brie or intense as aged cheese?',
      guideXLeft: '🍋 Lemon & vinegar', guideXMid: '🫒 Olive oil & salt', guideXRight: '🥛 Creamy mayo',
      guideYLeft: '🍵 Tea / water', guideYMid: '☕ Cappuccino', guideYRight: '☕ Espresso',
      guideZLeft: '🧀 Brie / Mozzarella', guideZMid: '🧀 Young cheese', guideZRight: '🧀 Aged Parmesan / Gouda',
      userLabel: 'YOU',
      ariaLabel: 'Taste Profile Explorer',
      legendUser: 'Drag your position', legendTop3: 'Top 3 matches', legendOtherRec: 'Other matches', legendOther: 'Other wines',
      explanation: 'What flavours are you in the mood for?',
      loading: 'Loading wine data...',
      dragHint: 'Drag me →',
      matchLabel: 'match',
      winesShown: '{count} wines sorted by match'
    },
    fr: {
      axXPos: 'Doux / Rond', axXNeg: 'Frais / Acide',
      axYPos: 'Corsé / Puissant', axYNeg: 'Léger',
      axZPos: 'Intense / Complexe', axZNeg: 'Subtil',
      barTitleX: 'DOUCEUR / ACIDITÉ', barTitleY: 'CORPS / STRUCTURE', barTitleZ: 'INTENSITÉ / COMPLEXITÉ',
      barDescX: '🍋 Frais & acidulé ou 🥛 doux & crémeux ?', barDescY: '🍵 Léger comme du thé ou ☕ corsé comme un espresso ?', barDescZ: '🧀 Doux comme du brie ou intense comme du vieux fromage ?',
      guideXLeft: '🍋 Citron & vinaigre', guideXMid: '🫒 Huile d\'olive & sel', guideXRight: '🥛 Mayo crémeuse',
      guideYLeft: '🍵 Thé / eau', guideYMid: '☕ Cappuccino', guideYRight: '☕ Espresso',
      guideZLeft: '🧀 Brie / Mozzarella', guideZMid: '🧀 Fromage jeune', guideZRight: '🧀 Vieux Parmesan / Gouda',
      userLabel: 'VOUS',
      ariaLabel: 'Explorateur de profil gustatif',
      legendUser: 'Glissez votre position', legendTop3: 'Top 3 correspondances', legendOtherRec: 'Autres correspondances', legendOther: 'Autres vins',
      explanation: 'Quelles saveurs vous tentent ?',
      loading: 'Chargement des données...',
      dragHint: 'Glissez-moi →',
      matchLabel: 'correspondance',
      winesShown: '{count} vins triés par correspondance'
    },
    de: {
      axXPos: 'Süß / Rund', axXNeg: 'Frisch / Säuerlich',
      axYPos: 'Voll / Kräftig', axYNeg: 'Leicht',
      axZPos: 'Intensiv / Komplex', axZNeg: 'Subtil',
      barTitleX: 'SÜßE / SÄURE', barTitleY: 'KÖRPER / STRUKTUR', barTitleZ: 'INTENSITÄT / KOMPLEXITÄT',
      barDescX: '🍋 Frisch & herb oder 🥛 sanft & cremig?', barDescY: '🍵 Leicht wie Tee oder ☕ kräftig wie Espresso?', barDescZ: '🧀 Mild wie Brie oder intensiv wie alter Käse?',
      guideXLeft: '🍋 Zitrone & Essig', guideXMid: '🫒 Olivenöl & Salz', guideXRight: '🥛 Cremige Mayo',
      guideYLeft: '🍵 Tee / Wasser', guideYMid: '☕ Cappuccino', guideYRight: '☕ Espresso',
      guideZLeft: '🧀 Brie / Mozzarella', guideZMid: '🧀 Junger Käse', guideZRight: '🧀 Alter Parmesan / Gouda',
      userLabel: 'DU',
      ariaLabel: 'Geschmacksprofil-Explorer',
      legendUser: 'Ziehe deine Position', legendTop3: 'Top 3 Treffer', legendOtherRec: 'Weitere Treffer', legendOther: 'Andere Weine',
      explanation: 'Welche Aromen sprechen dich an?',
      loading: 'Weindaten laden...',
      dragHint: 'Ziehe mich →',
      matchLabel: 'Übereinstimmung',
      winesShown: '{count} Weine sortiert nach Übereinstimmung'
    }
  };

  function getLang() {
    var html = (document.documentElement.lang || 'nl').toLowerCase().split('-')[0];
    return TRANSLATIONS[html] ? html : 'nl';
  }
  function t(key) { return TRANSLATIONS[getLang()][key] || TRANSLATIONS.nl[key] || key; }

  // ── Colors ────────────────────────────────────────────
  var C = {
    user: '#FFCC37', userStroke: '#D4A017',
    rec: '#81B29A', recStroke: '#5A8F76',
    recOther: '#E07A5F', recOtherStroke: '#C4533B',
    other: '#C4C0B8', otherStroke: '#A0A098',
    axisX: '#E07A5F', axisY: '#3D405B', axisZ: '#81B29A',
    bg: '#FAFAF8'
  };

  // ── State ─────────────────────────────────────────────
  var allWinesRaw = [];      // Raw API data
  var allWinesNorm = [];     // Normalized {id, x, y, z, color}
  var axesConfig = [];       // [{key, min, max, color, ...}]
  var userPos = { x: 0.5, y: 0.5, z: 0.5 }; // Normalized 0..1 position per axis
  var scoredWines = [];      // Wines with scores, sorted
  var isDragging = null;     // Which axis is being dragged ('x','y','z')
  var debounceTimer = null;
  var _updatingGrid = false;  // Guard flag: prevents observer re-triggering during our DOM updates
  var _apiPromise = null;     // Cached API promise for parallel prefetch

  // ── Fetch wine data from API (promise-based, cacheable) ──
  function fetchWineData() {
    if (_apiPromise) return _apiPromise;
    var apiUrl = 'https://api.vinpop.fr';
    var metaEl = document.querySelector('meta[name="api-url"]');
    if (metaEl && metaEl.content) apiUrl = metaEl.content;

    _apiPromise = fetch(apiUrl + '/api/wines/getMapAll')
      .then(function (res) {
        if (!res.ok) throw new Error('API error ' + res.status);
        return res.json();
      });
    return _apiPromise;
  }

  // ── Normalize wine coordinates into 0..1 ─────────────
  function normalizeWines() {
    var xVals = allWinesRaw.map(function (w) { return parseFloat(w.map_x) || 0; });
    var yVals = allWinesRaw.map(function (w) { return parseFloat(w.map_y) || 0; });
    var zVals = allWinesRaw.map(function (w) { return parseFloat(w.map_intensity) || 0; });

    var ranges = {
      x: { min: Math.min.apply(null, xVals), max: Math.max.apply(null, xVals) },
      y: { min: Math.min.apply(null, yVals), max: Math.max.apply(null, yVals) },
      z: { min: Math.min.apply(null, zVals), max: Math.max.apply(null, zVals) }
    };

    // Add padding
    ['x', 'y', 'z'].forEach(function (k) {
      var pad = (ranges[k].max - ranges[k].min) * 0.05;
      if (pad < 0.001) pad = 0.5;
      ranges[k].min -= pad;
      ranges[k].max += pad;
    });

    axesConfig = [
      { key: 'x', color: C.axisX, min: ranges.x.min, max: ranges.x.max },
      { key: 'y', color: C.axisY, min: ranges.y.min, max: ranges.y.max },
      { key: 'z', color: C.axisZ, min: ranges.z.min, max: ranges.z.max }
    ];

    allWinesNorm = allWinesRaw.map(function (w) {
      var wid = String(w.wine_id);
      // Get color from API or fall back to shopCatalog wineColor
      var rawColor = (w.color || '').toUpperCase();
      if (!rawColor || (rawColor !== 'R' && rawColor !== 'W')) {
        var catalog = window.shopCatalog || {};
        var entry = catalog[wid];
        if (entry && entry.wineColor) {
          var wc = entry.wineColor.toLowerCase();
          if (wc.indexOf('red') !== -1 || wc.indexOf('rood') !== -1 || wc.indexOf('rode') !== -1 || wc.indexOf('rouge') !== -1) rawColor = 'R';
          else if (wc.indexOf('white') !== -1 || wc.indexOf('wit') !== -1 || wc.indexOf('blanc') !== -1) rawColor = 'W';
          else rawColor = '';
        }
      }
      return {
        id: wid,
        x: normalize(parseFloat(w.map_x) || 0, ranges.x),
        y: normalize(parseFloat(w.map_y) || 0, ranges.y),
        z: normalize(parseFloat(w.map_intensity) || 0, ranges.z),
        color: rawColor
      };
    });

    // Set user position to the median of all wine positions (center of data)
    userPos.x = median(allWinesNorm.map(function (w) { return w.x; }));
    userPos.y = median(allWinesNorm.map(function (w) { return w.y; }));
    userPos.z = median(allWinesNorm.map(function (w) { return w.z; }));

    scoreAllWines();
  }

  function normalize(val, range) {
    return (val - range.min) / (range.max - range.min);
  }

  function median(arr) {
    var s = arr.slice().sort(function (a, b) { return a - b; });
    var mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  }

  // ── Score wines based on user position ────────────────
  function scoreAllWines() {
    scoredWines = allWinesNorm.map(function (w) {
      var dx = w.x - userPos.x;
      var dy = w.y - userPos.y;
      var dz = w.z - userPos.z;
      var dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      // Max possible distance is sqrt(3) ≈ 1.732 in normalized space
      var score = Math.max(0, Math.round(100 * (1 - dist / 1.732)));
      return { id: w.id, x: w.x, y: w.y, z: w.z, color: w.color, score: score, dist: dist };
    });
    scoredWines.sort(function (a, b) { return b.score - a.score; });

    // ── Color-balance: pick top 3 red + top 3 white ──
    // Always show exactly 6 wines (3 of each color), sorted by score.
    if (scoredWines.length >= 6) {
      var topReds = scoredWines.filter(function (w) { return (w.color || '').toUpperCase() === 'R'; }).slice(0, 3);
      var topWhites = scoredWines.filter(function (w) { return (w.color || '').toUpperCase() === 'W'; }).slice(0, 3);
      var top6 = topReds.concat(topWhites);
      top6.sort(function (a, b) { return b.score - a.score; });

      var top6Ids = {};
      top6.forEach(function (w) { top6Ids[w.id] = true; });
      var rest = scoredWines.filter(function (w) { return !top6Ids[w.id]; });

      scoredWines = top6.concat(rest);
      console.log('🎨 V2: Top 6 → ' + topReds.length + 'R + ' + topWhites.length + 'W');
    }
  }

  // ── Build the interactive map ─────────────────────────
  function renderMap() {
    var anchor = document.getElementById('js-filter-button') ||
                 document.getElementById('js-wine-results') ||
                 document.getElementById('facets-results');
    if (!anchor || !anchor.parentNode) {
      console.log('Wine Taste Map V2: no injection point');
      return;
    }

    // Remove any existing map
    var existing = document.getElementById('wine-taste-map-v2');
    if (existing) existing.remove();

    var isMobile = window.innerWidth <= 600;

    // ── Build wrapper ──
    var wrapper = document.createElement('div');
    wrapper.id = 'wine-taste-map-v2';
    wrapper.className = 'wine-taste-map-v2';

    var body = document.createElement('div');
    body.className = 'wine-taste-map-v2__body';

    // ── Explanation (top) ──
    var explEl = document.createElement('h2');
    explEl.className = 'wtm2-explanation';
    explEl.textContent = t('explanation');
    body.appendChild(explEl);

    // ── Build the 3 slider bars ──
    var axesDefs = [
      { key: 'x', title: t('barTitleX'), desc: t('barDescX'), negLabel: t('axXNeg'), posLabel: t('axXPos'), color: C.axisX, gradColor: '#E07A5F', guideLeft: t('guideXLeft'), guideMid: t('guideXMid'), guideRight: t('guideXRight') },
      { key: 'y', title: t('barTitleY'), desc: t('barDescY'), negLabel: t('axYNeg'), posLabel: t('axYPos'), color: C.axisY, gradColor: '#3D405B', guideLeft: t('guideYLeft'), guideMid: t('guideYMid'), guideRight: t('guideYRight') },
      { key: 'z', title: t('barTitleZ'), desc: t('barDescZ'), negLabel: t('axZNeg'), posLabel: t('axZPos'), color: C.axisZ, gradColor: '#81B29A', guideLeft: t('guideZLeft'), guideMid: t('guideZMid'), guideRight: t('guideZRight') }
    ];

    axesDefs.forEach(function (axDef) {
      var axisSection = document.createElement('div');
      axisSection.className = 'wtm2-axis';
      axisSection.dataset.axis = axDef.key;

      // Track container (holds bar + dots + user cursor)
      var trackContainer = document.createElement('div');
      trackContainer.className = 'wtm2-track';
      trackContainer.dataset.axis = axDef.key;

      // Track bar
      var trackBar = document.createElement('div');
      trackBar.className = 'wtm2-track__bar';
      trackBar.style.background = 'linear-gradient(to right, ' + axDef.gradColor + '0d, ' + axDef.gradColor + '2e)';
      trackBar.style.borderColor = axDef.gradColor + '40';
      trackContainer.appendChild(trackBar);

      // Center line
      var centerLine = document.createElement('div');
      centerLine.className = 'wtm2-track__center';
      centerLine.style.borderColor = axDef.gradColor + '33';
      trackContainer.appendChild(centerLine);

      // Wine dots container (rendered later)
      var dotsContainer = document.createElement('div');
      dotsContainer.className = 'wtm2-track__dots';
      dotsContainer.dataset.axis = axDef.key;
      trackContainer.appendChild(dotsContainer);

      // User cursor (draggable)
      var cursor = document.createElement('div');
      cursor.className = 'wtm2-cursor';
      cursor.dataset.axis = axDef.key;
      cursor.setAttribute('role', 'slider');
      cursor.setAttribute('aria-label', axDef.title);
      cursor.setAttribute('aria-valuemin', '0');
      cursor.setAttribute('aria-valuemax', '100');
      cursor.setAttribute('aria-valuenow', Math.round(userPos[axDef.key] * 100));
      cursor.setAttribute('tabindex', '0');

      var cursorInner = document.createElement('div');
      cursorInner.className = 'wtm2-cursor__inner';

      var arrowLeft = document.createElement('span');
      arrowLeft.className = 'wtm2-cursor__arrow wtm2-cursor__arrow--left';
      arrowLeft.textContent = '◀';

      var arrowRight = document.createElement('span');
      arrowRight.className = 'wtm2-cursor__arrow wtm2-cursor__arrow--right';
      arrowRight.textContent = '▶';

      var cursorLabel = document.createElement('span');
      cursorLabel.className = 'wtm2-cursor__label';
      cursorLabel.innerHTML = '<svg viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path d="M10 2h4v5.2c1.8.8 3 2.6 3 4.8v8c0 1.1-.9 2-2 2H9c-1.1 0-2-.9-2-2v-8c0-2.2 1.2-4 3-4.8V2zm1.5 1v4h1V3h-1z"/></svg>';

      cursorInner.appendChild(cursorLabel);
      cursor.appendChild(arrowLeft);
      cursor.appendChild(cursorInner);
      cursor.appendChild(arrowRight);
      trackContainer.appendChild(cursor);

      axisSection.appendChild(trackContainer);

      // Guide labels row (quiz examples)
      var guideRow = document.createElement('div');
      guideRow.className = 'wtm2-axis__guides';
      var guideL = document.createElement('span');
      guideL.className = 'wtm2-axis__guide wtm2-axis__guide--left';
      guideL.textContent = axDef.guideLeft;
      var guideM = document.createElement('span');
      guideM.className = 'wtm2-axis__guide wtm2-axis__guide--mid';
      guideM.textContent = axDef.guideMid;
      var guideR = document.createElement('span');
      guideR.className = 'wtm2-axis__guide wtm2-axis__guide--right';
      guideR.textContent = axDef.guideRight;
      guideRow.appendChild(guideL);
      guideRow.appendChild(guideM);
      guideRow.appendChild(guideR);
      axisSection.appendChild(guideRow);

      body.appendChild(axisSection);
    });

    // ── Legend (only on playlist page) ──
    var isTunerPage = window.location.pathname.includes('/collections/step-1-taste-tuner') || window.location.pathname.includes('/collections/quizz-tasting');
    if (!isTunerPage) {
      var legend = document.createElement('div');
      legend.className = 'wtm2-legend';
      var dotStyle = 'width:12px;height:12px;border-radius:50%;display:inline-block;flex-shrink:0;';
      var itemStyle = 'display:flex;align-items:center;gap:5px;font-size:11px;color:#666;font-weight:500;';
      legend.innerHTML =
        '<span style="' + itemStyle + '"><span style="width:16px;height:16px;background:#FFD700;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;border:1px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.2);"><svg viewBox=\'0 0 24 24\' fill=\'#fff\' width=\'10\' height=\'10\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M10 2h4v5.2c1.8.8 3 2.6 3 4.8v8c0 1.1-.9 2-2 2H9c-1.1 0-2-.9-2-2v-8c0-2.2 1.2-4 3-4.8V2zm1.5 1v4h1V3h-1z\'/></svg></span>' + t('legendUser') + '</span>' +
        '<span style="' + itemStyle + '"><span style="' + dotStyle + 'background:#81B29A;border:2px solid #5A8F76;"></span>' + t('legendTop3') + '</span>' +
        '<span style="' + itemStyle + '"><span style="' + dotStyle + 'background:#E07A5F;border:2px solid #C4533B;"></span>' + t('legendOtherRec') + '</span>' +
        '<span style="' + itemStyle + '"><span style="' + dotStyle + 'background:#C4C0B8;border:1px solid #AAA;opacity:0.6;"></span>' + t('legendOther') + '</span>';
      body.appendChild(legend);
    }

    // ── Status bar (only on playlist page) ──
    if (!isTunerPage) {
      var statusBar = document.createElement('div');
      statusBar.className = 'wtm2-status';
      statusBar.id = 'wtm2-status';
      body.appendChild(statusBar);
    }

    wrapper.appendChild(body);

    // ── Inject into DOM ──
    anchor.parentNode.insertBefore(wrapper, anchor);

    // Position cursors and render dots (grid update deferred until manager ready)
    updateCursorPositions();
    renderWineDots();

    // ── Setup drag interactions ──
    setupDragInteractions();
    setupKeyboardInteractions();

    // Animate in
    requestAnimationFrame(function () {
      wrapper.classList.add('wine-taste-map-v2--visible');
    });

    // ── Sticky scroll behavior ──
    setupStickyScroll(wrapper);

    // ── Watch for grid re-renders (e.g. after filter apply or manager takeover) ──
    var _activeGridObserver = null;
    function observeGrid() {
      var gridContainer = document.getElementById('js-wine-results') ||
                          document.querySelector('#facets-results ul') ||
                          document.querySelector('#product-grid');
      if (!gridContainer) return;
      if (_activeGridObserver) { _activeGridObserver.disconnect(); _activeGridObserver = null; }
      var gridObserver = new MutationObserver(function () {
        if (_updatingGrid) return; // ignore mutations caused by our own updates
        clearTimeout(gridObserver._timer);
        gridObserver._timer = setTimeout(function () {
          updateProductGrid();
        }, 200);
      });
      gridObserver.observe(gridContainer, { childList: true, subtree: false });
      _activeGridObserver = gridObserver;
    }
    // Observe now, and re-observe when manager creates #js-wine-results
    observeGrid();
    var bodyObs = new MutationObserver(function () {
      if (document.getElementById('js-wine-results')) {
        observeGrid();
        bodyObs.disconnect();
      }
    });
    bodyObs.observe(document.body, { childList: true, subtree: true });

    console.log('✅ Wine Taste Map V2 rendered with', allWinesNorm.length, 'wines');
  }

  // ── Position user cursors ──
  function updateCursorPositions() {
    ['x', 'y', 'z'].forEach(function (axis) {
      var cursor = document.querySelector('.wtm2-cursor[data-axis="' + axis + '"]');
      if (cursor) {
        var pct = userPos[axis] * 100;
        cursor.style.left = pct + '%';
        cursor.setAttribute('aria-valuenow', Math.round(pct));
      }
    });
  }

  // ── Render wine dots on the tracks ──
  function renderWineDots() {
    // Limit other wines shown (non-recommended) to 30 for performance
    var recIds = {};
    scoredWines.slice(0, 16).forEach(function (w) { recIds[w.id] = true; });

    var others = allWinesNorm.filter(function (w) { return !recIds[w.id]; });
    if (others.length > 30) {
      others.sort(function () { return Math.random() - 0.5; });
      others = others.slice(0, 30);
    }

    ['x', 'y', 'z'].forEach(function (axis) {
      var container = document.querySelector('.wtm2-track__dots[data-axis="' + axis + '"]');
      if (!container) return;
      container.innerHTML = '';

      // Other dots (small, grey)
      others.forEach(function (w) {
        var dot = document.createElement('div');
        dot.className = 'wtm2-dot wtm2-dot--other';
        dot.style.left = (w[axis] * 100) + '%';
        dot.dataset.wineId = w.id;
        container.appendChild(dot);
      });

      // Recommended dots (top 16)
      var top3 = scoredWines.slice(0, 3);
      var otherRec = scoredWines.slice(3, 16);

      otherRec.forEach(function (w) {
        var dot = document.createElement('div');
        dot.className = 'wtm2-dot wtm2-dot--rec-other';
        dot.style.left = (w[axis] * 100) + '%';
        dot.dataset.wineId = w.id;
        dot.dataset.score = w.score;
        dot.title = getWineTooltip(w);
        container.appendChild(dot);
      });

      top3.forEach(function (w) {
        var dot = document.createElement('div');
        dot.className = 'wtm2-dot wtm2-dot--top3';
        dot.style.left = (w[axis] * 100) + '%';
        dot.dataset.wineId = w.id;
        dot.dataset.score = w.score;
        dot.title = getWineTooltip(w);
        container.appendChild(dot);
      });
    });
  }

  function getWineTooltip(w) {
    var catalog = window.shopCatalog || {};
    var entry = catalog[w.id];
    var name = entry ? entry.title : 'Wine #' + w.id;
    return name + ' — ' + w.score + '% ' + t('matchLabel');
  }

  // ── Drag interactions ─────────────────────────────────
  function setupDragInteractions() {
    var cursors = document.querySelectorAll('.wtm2-cursor');
    cursors.forEach(function (cursor) {
      // Mouse
      cursor.addEventListener('mousedown', startDrag);
      // Touch
      cursor.addEventListener('touchstart', startDrag, { passive: false });
    });

    // Also allow clicking on the track to set position
    var tracks = document.querySelectorAll('.wtm2-track');
    tracks.forEach(function (track) {
      track.addEventListener('click', function (e) {
        if (isDragging) return;
        var axis = track.dataset.axis;
        var rect = track.getBoundingClientRect();
        var pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        userPos[axis] = pct;
        onUserMoved();
      });
    });

    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', endDrag);
    document.addEventListener('touchmove', onDragMove, { passive: false });
    document.addEventListener('touchend', endDrag);
  }

  function startDrag(e) {
    e.preventDefault();
    var axis = e.currentTarget.dataset.axis;
    isDragging = axis;
    document.body.classList.add('wtm2-dragging');

    // Stop pulse animation on this cursor once touched
    var cursor = e.currentTarget;
    cursor.classList.add('wtm2-cursor--touched');
  }

  function onDragMove(e) {
    if (!isDragging) return;
    e.preventDefault();

    var clientX = e.touches ? e.touches[0].clientX : e.clientX;
    var track = document.querySelector('.wtm2-track[data-axis="' + isDragging + '"]');
    if (!track) return;

    var rect = track.getBoundingClientRect();
    var pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    userPos[isDragging] = pct;

    // Update cursor position immediately (smooth feel)
    var cursor = document.querySelector('.wtm2-cursor[data-axis="' + isDragging + '"]');
    if (cursor) {
      cursor.style.left = (pct * 100) + '%';
      cursor.setAttribute('aria-valuenow', Math.round(pct * 100));
    }

    // Debounce scoring + product update
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      onUserMoved();
    }, 80);
  }

  function endDrag() {
    if (isDragging) {
      isDragging = null;
      document.body.classList.remove('wtm2-dragging');
      // Final update
      onUserMoved();
    }
  }

  // ── Keyboard interactions ─────────────────────────────
  function setupKeyboardInteractions() {
    var cursors = document.querySelectorAll('.wtm2-cursor');
    cursors.forEach(function (cursor) {
      cursor.addEventListener('keydown', function (e) {
        var axis = cursor.dataset.axis;
        var step = e.shiftKey ? 0.1 : 0.02;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          userPos[axis] = Math.min(1, userPos[axis] + step);
          onUserMoved();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          userPos[axis] = Math.max(0, userPos[axis] - step);
          onUserMoved();
        }
      });
    });
  }

  // ── User position changed ─────────────────────────────
  function onUserMoved() {
    scoreAllWines();
    updateCursorPositions();
    renderWineDots();
    updateProductGrid();
  }

  // ── Update product grid based on scores ───────────────
  function updateProductGrid() {
    // Try manager's custom grid first, then native grid
    var grid = document.getElementById('js-wine-results') ||
               document.querySelector('#facets-results ul') ||
               document.querySelector('#product-grid');
    if (!grid) return;

    // Prevent MutationObserver feedback loop — our own DOM changes should not re-trigger
    _updatingGrid = true;

    // Find cards: manager cards (have data-vinpop-card-id) or native cards (have data-product-id on <li>)
    var cards = Array.from(grid.querySelectorAll('li[data-vinpop-card-id], li[data-product-id]'));
    // Exclude combo cards and starter pack wrappers
    cards = cards.filter(function (c) {
      return !c.classList.contains('vinpop-combo-card') && !c.classList.contains('vinpop-starter-pack-wrapper');
    });
    if (cards.length === 0) return;

    // Build score + color maps for products in the grid
    var scoreMap = {};
    var colorMap = {};
    scoredWines.forEach(function (w) {
      scoreMap[w.id] = w.score;
      colorMap[w.id] = (w.color || '').toUpperCase();
    });

    // Quizz-tasting mapping: blind product IDs → real wine IDs
    // On /collections/quizz-tasting, cards use blind product IDs but scores use real wine IDs
    function toRealWineId(pid) {
      if (!pid) return pid;
      if (scoreMap[pid] !== undefined) return pid; // already a real wine ID
      var qt = window.quizzTastingCatalog;
      if (qt && qt[pid] && qt[pid].productid) return String(qt[pid].productid);
      return pid;
    }

    // Extract product ID from each card (native or manager)
    function getProductId(card) {
      // Native card: data-product-id on <li>
      var pid = card.getAttribute('data-product-id');
      if (pid) return pid;
      // Manager card: data-product-id on the <form> inside
      var form = card.querySelector('form[data-product-id], .vinpop-ajax-form[data-product-id]');
      if (form) return form.getAttribute('data-product-id');
      // Try product link URL: /products/handle → lookup in shopCatalog
      var link = card.querySelector('a[href*="/products/"]');
      if (link && window.shopCatalog) {
        var href = link.getAttribute('href');
        var match = href.match(/\/products\/([^?#\/]+)/);
        if (match) {
          var handle = match[1];
          var catalog = window.shopCatalog;
          for (var id in catalog) {
            if (catalog[id].handle === handle) return id;
          }
        }
      }
      return null;
    }

    // Score and sort cards
    var scoredCards = cards.map(function (card) {
      var pid = getProductId(card);
      var wineId = toRealWineId(pid);
      var score = wineId && scoreMap[wineId] !== undefined ? scoreMap[wineId] : 0;
      return { card: card, pid: pid, wineId: wineId, score: score };
    });

    scoredCards.sort(function (a, b) { return b.score - a.score; });

    // Build top-6 set: top 3 red + top 3 white by score (already sorted)
    var catalog = window.shopCatalog || {};
    var cardTop6Ids = {};
    var cRedCount = 0, cWhiteCount = 0;

    function getWineColor(pid) {
      if (!pid) return '';
      var resolved = toRealWineId(pid);
      // Try scoredWines color first (using real wine ID)
      var col = colorMap[resolved] || '';
      if (col === 'R' || col === 'W') return col;
      // Fall back to shopCatalog (try real wine ID, then card ID)
      var entry = catalog[resolved] || catalog[pid];
      if (entry && entry.wineColor) {
        var wc = entry.wineColor.toLowerCase();
        if (wc.indexOf('red') !== -1 || wc.indexOf('rood') !== -1 || wc.indexOf('rode') !== -1 || wc.indexOf('rouge') !== -1) return 'R';
        if (wc.indexOf('white') !== -1 || wc.indexOf('wit') !== -1 || wc.indexOf('blanc') !== -1) return 'W';
      }
      return '';
    }

    scoredCards.forEach(function (item) {
      if (!item.pid) return;
      var col = getWineColor(item.pid);
      if (col === 'R' && cRedCount < 3) { cardTop6Ids[item.pid] = true; cRedCount++; }
      else if (col === 'W' && cWhiteCount < 3) { cardTop6Ids[item.pid] = true; cWhiteCount++; }
    });

    console.log('🎨 V2 grid: found', cards.length, 'cards,', Object.keys(cardTop6Ids).length, 'in top6 (' + cRedCount + 'R+' + cWhiteCount + 'W)');

    // Reorder in DOM by score (descending) — move all cards, visible or not
    scoredCards.forEach(function (item, index) {
      item.card.style.order = String(index);
      grid.appendChild(item.card);
    });

    // Show top 6, hide the rest, add badges
    var visibleIndex = 0;
    scoredCards.forEach(function (item) {
      var isTop6 = item.pid && cardTop6Ids[item.pid];
      if (isTop6) {
        item.card.style.display = '';
        item.card.style.removeProperty('display');
        addScoreBadge(item.card, item.score, visibleIndex);
        addMatchText(item.card, item.score, visibleIndex);
        visibleIndex++;
      } else {
        // Remove old badges from hidden cards
        item.card.querySelectorAll('.wtm2-badge').forEach(function (b) { b.remove(); });
        item.card.querySelectorAll('.wtm2-match-text').forEach(function (el) { el.remove(); });
        item.card.style.setProperty('display', 'none', 'important');
      }
    });

    // Update status
    var statusEl = document.getElementById('wtm2-status');
    if (statusEl) {
      statusEl.textContent = t('winesShown').replace('{count}', visibleIndex);
    }

    // Dispatch event for other scripts
    document.dispatchEvent(new CustomEvent('vinpop:v2-results-updated', {
      detail: { scores: scoredCards.map(function (s) { return { id: s.pid, score: s.score }; }) }
    }));

    // Re-enable grid observer after a tick (let pending mutations flush)
    setTimeout(function () { _updatingGrid = false; }, 50);
  }

  // ── Badge on product cards ────────────────────────────
  function addScoreBadge(card, score, rank) {
    // Remove existing V2 badge
    card.querySelectorAll('.wtm2-badge').forEach(function (b) { b.remove(); });

    var mediaLink = card.querySelector('a.product-tile__featured-media, a[href*="/products/"]');
    if (!mediaLink) return;
    mediaLink.style.position = 'relative';

    var isTop3 = rank < 3;
    var bgColor = isTop3 ? C.rec : C.recOther;
    var borderColor = isTop3 ? C.recStroke : C.recOtherStroke;

    var badge = document.createElement('div');
    badge.className = 'wtm2-badge';
    badge.innerHTML =
      '<div class="wtm2-badge__circle" style="background:' + bgColor + ';border-color:' + borderColor + ';">' +
        '<span class="wtm2-badge__score">' + score + '%</span>' +
      '</div>';
    mediaLink.appendChild(badge);
  }

  // ── Match text below product title ────────────────────
  function addMatchText(card, score, rank) {
    card.querySelectorAll('.wtm2-match-text').forEach(function (el) { el.remove(); });

    var tile = card.querySelector('.product-tile.mt-5, .product-tile');
    var h3 = tile ? tile.querySelector('h3') : null;
    if (!h3) return;

    var text = document.createElement('div');
    text.className = 'wtm2-match-text';

    var isTasteTuner = window.location.pathname.includes('/collections/step-1-taste-tuner') || window.location.pathname.includes('/collections/quizz-tasting');
    var isNL = getLang() === 'nl';

    if (isTasteTuner) {
      // Taste Tuner: % = likelihood to enjoy based on slider preferences
      if (score >= 95) {
        text.textContent = '🎯 ' + score + '% — ' + (isNL ? 'je gaat hier vrijwel zeker van genieten' : 'you will almost certainly love this one');
      } else if (score >= 90) {
        text.textContent = '🎯 ' + score + '% — ' + (isNL ? 'grote kans dat dit jouw smaak is' : 'very likely to match your taste');
      } else if (score >= 80) {
        text.textContent = '🎯 ' + score + '% — ' + (isNL ? 'past goed bij jouw smaakprofiel' : 'fits your taste profile well');
      } else if (score >= 70) {
        text.textContent = '🎯 ' + score + '% — ' + (isNL ? 'kan je verrassen, probeer het eens' : 'might surprise you, give it a try');
      } else {
        text.textContent = '🎯 ' + score + '% ' + t('matchLabel');
      }
    } else {
      // My Playlist: % = similarity between wines
      if (score >= 95) {
        text.textContent = '🎯 ' + score + '% — ' + (isNL ? 'verschil alleen merkbaar voor professionele proevers' : 'differences only perceived by professional tasters');
      } else if (score >= 90) {
        text.textContent = '🎯 ' + score + '% — ' + (isNL ? 'verschil ook merkbaar voor wijnkenners' : 'differences also perceived by wine connoisseurs');
      } else if (score >= 80) {
        text.textContent = '🎯 ' + score + '% — ' + (isNL ? 'de smaak zit in dezelfde lijn' : 'the taste is in the same range');
      } else if (score >= 70) {
        text.textContent = '🎯 ' + score + '% — ' + (isNL ? 'dit is een nieuw avontuur' : 'this is a new adventure');
      } else {
        text.textContent = '🎯 ' + score + '% ' + t('matchLabel');
      }
    }

    h3.insertAdjacentElement('afterend', text);
  }

  // ── Sticky scroll: position:fixed + placeholder + scroll check ──
  // Normal mode: wrapper is in regular document flow.
  // Compact mode: wrapper becomes position:fixed below the header,
  //   a placeholder preserves the layout space.
  // Trigger: filter button bottom goes behind the header.
  // Un-trigger: scroll back above saved threshold.
  // No IntersectionObserver → no feedback loop → no flickering.
  function setupStickyScroll(wrapper) {
    function getHeaderHeight() {
      var headerSection = document.querySelector('[data-sticky-header]');
      if (headerSection) {
        var headerEl = headerSection.closest('.shopify-section') || headerSection;
        if (headerEl.offsetHeight) return headerEl.offsetHeight;
      }
      var sec = document.getElementById('shopify-section-header') ||
                document.querySelector('.shopify-section-header');
      return sec ? (sec.offsetHeight || 0) : 0;
    }

    // Create placeholder (hidden by default, shows when wrapper goes fixed)
    var placeholder = document.createElement('div');
    placeholder.style.cssText = 'display:none;margin:0;padding:0;border:0;';
    wrapper.parentNode.insertBefore(placeholder, wrapper.nextSibling);

    var isCompact = false;
    var ticking = false;
    var compactScrollY = 0; // saved scrollY when entering compact

    function goCompact() {
      if (isCompact) return;
      var headerH = getHeaderHeight();
      var wrapperH = wrapper.offsetHeight;
      placeholder.style.height = wrapperH + 'px';
      placeholder.style.display = 'block';
      // Un-trigger threshold: current scroll minus the compact bar height (~60px)
      // so user only needs to scroll back a little to reveal normal view
      compactScrollY = (window.pageYOffset || document.documentElement.scrollTop) - 80;
      wrapper.style.top = headerH + 'px';
      wrapper.classList.add('wine-taste-map-v2--sticky');
      isCompact = true;
    }

    function goNormal() {
      if (!isCompact) return;
      wrapper.classList.remove('wine-taste-map-v2--sticky');
      wrapper.style.top = '';
      placeholder.style.display = 'none';
      placeholder.style.height = '';
      isCompact = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (!isCompact) {
          // Check if filter button has scrolled behind the header
          var filterBtn = document.getElementById('js-filter-button');
          if (!filterBtn) return;
          var headerH = getHeaderHeight();
          var btnRect = filterBtn.getBoundingClientRect();
          // Trigger when the TOP of the button reaches the header bottom
          if (btnRect.top < headerH) {
            goCompact();
          }
        } else {
          // Un-compact when scrolling back above saved position
          var scrollY = window.pageYOffset || document.documentElement.scrollTop;
          if (scrollY < compactScrollY) {
            goNormal();
          }
        }
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ── Check if V2 mode is active ────────────────────────
  function isV2Active() {
    var pathname = window.location.pathname;
    // Activate ONLY on /collections/step-1-taste-tuner.
    // /collections/quizz-tasting has its own dedicated renderer
    // (taste-profile-quiz-tasting.js) and must NOT be overridden by V2.
    if (pathname.includes('/collections/step-1-taste-tuner')) return true;
    return false;
  }

  // ── Hide existing quiz UI when V2 is active ───────────
  function hideExistingQuizUI() {
    // Detect inline-quiz mode (e.g. /collections/quizz-tasting). In inline mode the
    // existing quiz UI (popup + demo banner with Retake/Start button) IS the user's
    // entry point, so we must NOT hide it.
    var inlinePopup = document.querySelector('#taste-profile-popup.taste-profile-popup--inline');
    var hasInlineQuiz = !!inlinePopup;

    // Hide the taste-profile-trigger section (quiz banner + popup trigger)
    var triggerSection = document.querySelector('.section-taste-profile-trigger, [class*="taste-profile-trigger"]');
    if (triggerSection) triggerSection.style.display = 'none';

    // Hide the original wine-taste-map if it exists
    var oldMap = document.getElementById('wine-taste-map');
    if (oldMap) oldMap.style.display = 'none';

    // Hide old score-percentage badges on product cards
    document.querySelectorAll('.vinpop-score-badge').forEach(function (b) { b.style.display = 'none'; });

    // Hide the combo CTA card ("De Quiz was pas stap één…")
    document.querySelectorAll('.vinpop-combo-card').forEach(function (c) { c.style.display = 'none'; });

    // Hide demo mode elements (banner, bottom CTA bar)
    var demoCta = document.querySelector('.demo-mode-bottom-cta');
    if (demoCta) demoCta.remove();
    if (!hasInlineQuiz) {
      var demoBanner = document.querySelector('.demo-mode-banner');
      if (demoBanner) demoBanner.style.display = 'none';
    }

    // Prevent the quiz auto-popup from opening
    sessionStorage.setItem('vinpop_v2_active', 'true');
    window.__vinpopQuizDemoMode = false;

    // Hide quiz popup if somehow visible — but NEVER hide the inline quiz on quizz-tasting,
    // because the inline popup IS the quiz UI the user is meant to interact with.
    var popup = document.getElementById('taste-profile-popup');
    if (popup && !popup.classList.contains('taste-profile-popup--inline')) {
      popup.style.display = 'none';
    }
    document.body.style.overflow = '';

    // Observe for late-injected badges, combo cards, and demo UI
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.classList && n.classList.contains('vinpop-score-badge')) n.style.display = 'none';
          if (n.classList && n.classList.contains('vinpop-combo-card')) n.style.display = 'none';
          if (n.classList && n.classList.contains('demo-mode-bottom-cta')) n.remove();
          if (!hasInlineQuiz && n.classList && n.classList.contains('demo-mode-banner')) n.style.display = 'none';
          n.querySelectorAll && n.querySelectorAll('.vinpop-score-badge, .vinpop-combo-card').forEach(function (el) { el.style.display = 'none'; });
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Trigger the recommendation manager to render the grid ──
  // V2 feeds scored wines to the manager so it creates the product grid,
  // filter drawer, and top-3 starter pack background.
  function triggerManagerRender(callback) {
    if (!window.wineManager || !window.shopCatalog) {
      callback();
      return;
    }

    // If the manager already rendered its grid, skip
    if (document.getElementById('js-wine-results')) {
      callback();
      return;
    }

    // Build results array in the format the manager expects
    var results = scoredWines.map(function (w) {
      return { wine_id: w.id, score: w.score, distance: w.dist, color: w.color };
    });

    window.wineManager.mode = 'taste-map';
    window.wineManager.renderRecommendations(results)
      .then(function () {
        console.log('✅ V2: Manager grid rendered with filters & top-3');
        callback();
      })
      .catch(function (err) {
        console.error('V2: Manager render failed, using native grid:', err);
        callback();
      });
  }

  // ── Bootstrap ─────────────────────────────────────────
  function init() {
    if (!isV2Active()) return;

    console.log('🍷 Wine Taste Map V2: Initializing...');
    hideExistingQuizUI();

    var loadingEl = document.createElement('div');
    loadingEl.id = 'wtm2-loading';
    loadingEl.className = 'wtm2-loading';
    loadingEl.textContent = t('loading');

    var anchor = document.getElementById('facets-results') ||
                 document.getElementById('js-wine-results');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(loadingEl, anchor);
    }

    // Use prefetched API data (fetch started at script load, in parallel with catalog wait)
    fetchWineData().then(function (wines) {
      allWinesRaw = wines;
      normalizeWines();

      var loadEl = document.getElementById('wtm2-loading');
      if (loadEl) loadEl.remove();

      // Render sliders + dots immediately (no grid dependency)
      renderMap();

      // Then trigger manager to build the product grid and update it
      triggerManagerRender(function () {
        updateProductGrid();
      });
    }).catch(function (err) {
      console.error('Wine Taste Map V2: Failed to fetch wines', err);
    });
  }

  // Wait for DOM + catalog (fast polling, no initial delay)
  function waitForReady() {
    if (!isV2Active()) return;

    var hasCatalog = window.shopCatalog && Object.keys(window.shopCatalog).length > 0;

    if (hasCatalog) {
      init();
    } else {
      setTimeout(waitForReady, 50);
    }
  }

  // Start API fetch immediately (runs in parallel with catalog wait)
  if (isV2Active()) fetchWineData();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForReady);
  } else {
    waitForReady();
  }

})();
